'use client';

import { useState, useTransition, useEffect, useRef, use, Suspense } from 'react';
import dynamic from 'next/dynamic';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Icon } from '@/components/ui/icon';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { NotePanel } from '@/components/ui/note-panel';
import { Textarea } from '@/components/ui/input';
import type { VariantType } from '@/db/schema/enums';
import type { RecipeMetadata, VariantMetadata, FeasibleVariantMetadata, StepWithTranslation, IngredientWithDetails, RecipeBody, RecipeLineage, RecipeLineageLink } from '@/lib/data/recipes';
import { fetchVariantDetailAction, generateVariantAction, proposeVariantOptionsAction } from '@/lib/actions/recipes';
import { variantOptionToDirection, type VariantOption } from '@/lib/validators/variant-options';
import { ActionError } from '@/components/ui/action-error';
import { DirectionCards } from '@/components/ui/direction-cards';
import { flyToBell } from '@/lib/utils/fly-to-bell';
import { shareOrCopyLink } from '@/lib/utils/share-recipe';
import { useToast } from '@/components/ui/toast';
import { wakeNotificationBell } from '@/components/layout/notification-bell';
import { RecipeHero } from '@/components/recipe/recipe-hero';
import { CookbookPicker } from '@/components/recipe/cookbook-picker';
import { VariantSelector } from '@/components/recipe/variant-selector';
import { RelatedRecipeLink } from '@/components/ui/related-recipe-link';
import type { DietaryItem } from '@/components/ui/dietary-indicators';
import { ServingCounter } from '@/components/recipe/serving-counter';
import { IngredientList, hasDisplayableKcal } from '@/components/recipe/ingredient-list';
import { IngredientListStagger } from '@/components/recipe/ingredient-list-stagger';
import { StepList } from '@/components/recipe/step-list';
import { NutritionPanel } from '@/components/recipe/nutrition-panel';
import { SkeletonGroup, Skeleton } from '@/components/ui/skeleton';
import { pollBackoffDelay } from '@/lib/utils/poll-backoff';
import styles from './recipe-detail-client.module.css';

// Modals are heavy (form state, full-screen overlays) and only mount on user
// action — keep them out of the initial bundle so first paint hydrates faster.
const AddToMealPlanModal = dynamic(
  () => import('@/components/recipe/add-to-meal-plan-modal').then((m) => m.AddToMealPlanModal),
);
const CookMode = dynamic(
  () => import('@/components/recipe/cook-mode').then((m) => m.CookMode),
);
const RecipeAdminModal = dynamic(
  () => import('@/components/recipe/recipe-admin-modal').then((m) => m.RecipeAdminModal),
);

/**
 * `derivation_note` holds either a preset key the remix panel wrote
 * ('vegetarian', 'gluten_free', …) or the user's own free text. Preset keys get
 * translated; anything else is the user's words and is shown verbatim.
 *
 * Deliberately a lookup rather than `t('...' + note)` — next-intl throws on a
 * missing key, so interpolating free text into a key path would turn any
 * free-text remix into a render error.
 */
const DERIVATION_PRESET_KEYS = new Set(['vegetarian', 'vegan', 'gluten_free', 'dairy_free']);

/** How long the "re-shooting your photo…" toast may stay up before it admits
 *  the run is late. Matches the hero poll's own 60 × 5s ceiling, so the two
 *  cannot disagree about when a regeneration has taken too long. */
const IMAGE_REGEN_TIMEOUT_MS = 5 * 60_000;

interface RecipeDetailClientProps {
  meta: RecipeMetadata;
  /** Creator display name for the hero byline; null when unknown. */
  authorName: string | null;
  variants: VariantMetadata[];
  initialActiveVariant: FeasibleVariantMetadata;
  /** Remix relationships in both directions (#614). */
  lineage: RecipeLineage;
  /**
   * Promise resolving to steps + ingredients for the initial active variant.
   * Passed unawaited from the server so the hero/selector can commit before the
   * heavier queries land — the body subtree suspends on this Promise via use().
   * Once resolved (or once the user switches variants), the data is lifted
   * into local state and this Promise is no longer consulted.
   */
  bodyPromise: Promise<RecipeBody>;
  lang: 'en' | 'de';
  /** Slugs of dietary preferences the current user has active (e.g. ['vegan', 'hashimoto']). */
  activeDietaryPreferences: string[];
  unitSystem: 'metric' | 'imperial';
  initialCookbooks: { id: string; title: string; isDefault: boolean; containsRecipe: boolean }[];
  /** Variants with an active derive_variant job for the current user — server-fetched
   *  so the variant tab can surface a "Generating…" state across navigation. */
  initialActiveDeriveTargets?: Array<'quick' | 'classic' | 'bougie'>;
  /** User's preferred default serving count from profile settings (1–12). */
  defaultServings?: number;
  /** Serving count carried in `?servings=`, already validated server-side —
   *  set when arriving from a planned meal so the page opens scaled to the
   *  portions that meal is planned for. Outranks defaultServings (#743). */
  requestedServings?: number;
  /** True if the visitor is the recipe owner (creator). Gates variant
   *  iteration (edit + version history + revert) — owner-only, not admins. */
  isOwner?: boolean;
  /** True if the visitor is an admin. With isOwner, gates the unified
   *  "Rezeptverwaltung" management modal and its per-action items. */
  isAdmin?: boolean;
  /** True if the recipe is published + public. Gates the Share action on
   *  the hero corner — sharing a draft or private recipe would 404 the
   *  recipient. */
  isShareable?: boolean;
  /** True when a user session is present. Gates the Cook / Save / Plan
   *  actions and variant generation — logged-out visitors of a public recipe
   *  get a read-only view with a sign-in prompt instead (the underlying
   *  Server Actions require auth). */
  isLoggedIn?: boolean;
  /** True when the viewer's language (German) is not yet available because the
   *  recipe's translation is still in flight — either the recipe is held at
   *  pending_review, or the shell (title, description, per-variant hero copy)
   *  resolved from English (#954). Drives a "translating…" toast + auto-refresh
   *  once German lands. Server sets it only for lang==='de' — English is written
   *  first, so English viewers never see this. */
  translationPending?: boolean;
}

export function RecipeDetailClient({
  meta,
  authorName,
  variants,
  initialActiveVariant,
  bodyPromise,
  lang,
  activeDietaryPreferences,
  unitSystem,
  defaultServings,
  requestedServings,
  initialCookbooks,
  lineage,
  initialActiveDeriveTargets = [],
  isOwner = false,
  isAdmin = false,
  isShareable = false,
  isLoggedIn = false,
  translationPending = false,
}: RecipeDetailClientProps) {
  const canManage = isOwner || isAdmin;
  const t = useTranslations();
  const derivationLabel = (note: string) =>
    DERIVATION_PRESET_KEYS.has(note) ? t(`recipes.remix.preset_${note}`) : note;

  /**
   * Dietary glyphs for a cross-linked recipe. Vegan implies vegetarian, so only
   * the stricter glyph is shown — two leaves on one card would read as two
   * separate claims rather than one stronger one.
   */
  const dietaryItemsFor = (link: RecipeLineageLink): DietaryItem[] => {
    if (link.isVegan) return [{ key: 'vegan', label: t('dietary.vegan') }];
    if (link.isVegetarian) return [{ key: 'vegetarian', label: t('dietary.vegetarian') }];
    return [];
  };
  const { toast, updateToast, dismissToast } = useToast();
  const router = useRouter();
  const searchParams = useSearchParams();
  const addToDateRaw = searchParams?.get('addToDate');
  const addToDate = addToDateRaw && /^\d{4}-\d{2}-\d{2}$/.test(addToDateRaw) ? addToDateRaw : undefined;
  const [isPending, startTransition] = useTransition();

  const [activeVariant, setActiveVariant] = useState<FeasibleVariantMetadata>(initialActiveVariant);
  // The translation poll below deliberately keeps `activeVariant` out of its
  // deps — a tab switch must not restart the toast — so it reads the variant
  // the user is actually looking at through this ref instead of a closure that
  // was captured when the poll started (#917).
  const activeVariantRef = useRef(activeVariant);
  useEffect(() => {
    activeVariantRef.current = activeVariant;
  }, [activeVariant]);
  // null until either bodyPromise resolves (initial paint) or the user switches
  // variants. CookMode + the body content both read from here once populated.
  const [body, setBody] = useState<RecipeBody | null>(null);
  // What the counter falls back to whenever it resets. A meal-plan arrival
  // (`?servings=`) outranks the profile default — the user already said how
  // many portions this dish is for, and that answer should survive a variant
  // switch rather than snapping back to the generic preference.
  const preferredServings = requestedServings ?? defaultServings;
  const [selectedServings, setSelectedServings] = useState(preferredServings ?? initialActiveVariant.servings);

  const [showMealPlanModal, setShowMealPlanModal] = useState(false);
  const [showCookMode, setShowCookMode] = useState(false);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [mobileSection, setMobileSection] = useState<'ingredients' | 'steps'>('ingredients');

  // Hero-image regeneration in flight (#642). Set when the management hub
  // queues one, cleared when the hero reports the run finished. The page no
  // longer polls for it: the hero's own `useGeneratedImage` does, told about
  // the run through `imageGenerationInFlight` (#751). Two loops against the
  // same endpoint is what that hook exists to prevent (#646).
  const [imageRegen, setImageRegen] = useState<{ variantId: string } | null>(null);
  // The re-shot photo once it lands, tagged with the variant it belongs to so a
  // tab switch mid-run can't paint it onto the wrong variant.
  const [freshHeroImage, setFreshHeroImage] = useState<{
    variantId: string;
    url: string;
  } | null>(null);

  // What the hero is actually showing right now. A landed regeneration wins
  // over the server-rendered url, which stays stale until the page is fetched
  // again — and this, not `activeVariant.imageUrl`, is the baseline a SECOND
  // regeneration must compare against, or the poller would see the first run's
  // photo as "the new one" and resolve instantly.
  const heroImageUrl =
    freshHeroImage?.variantId === activeVariant.id
      ? freshHeroImage.url
      : activeVariant.imageUrl;

  const [generatingVariant, setGeneratingVariant] = useState<VariantType | null>(null);
  const [variantGenError, setVariantGenError] = useState<string | null>(null);

  // Variant-steering dialog: when the user clicks "Generate", we open a small
  // dialog instead of triggering immediately, so they can optionally supply
  // free-text notes that flow into the derive-variant prompt as a USER_MODIFICATIONS
  // block. Empty notes → behaves identically to the old immediate trigger.
  const [pendingGenerate, setPendingGenerate] = useState<{
    variantType: VariantType;
    originRect: DOMRect;
  } | null>(null);
  const [pendingNotes, setPendingNotes] = useState('');

  // "Guide me" sub-flow inside the steering dialog (#909): a cheap proposal
  // job suggests concrete directions for the target tier, the user accepts/
  // rejects them, and the decisions ride into generateVariantAction. null =
  // the plain dialog (free-text only), exactly the pre-#909 behaviour.
  // `chosen` holds the ids the user switched ON. Off is not a third state:
  // the user has seen every direction, so anything left off is one they
  // decided against and is sent as rejected — unless NOTHING is on, which
  // means "no opinion, chef's choice" and sends no directions at all.
  const [guideState, setGuideState] = useState<
    | { phase: 'loading'; jobId: string }
    | { phase: 'options'; options: VariantOption[]; chosen: string[] }
    | { phase: 'error'; error: string | null }
    | null
  >(null);

  // Tracks variants with an active derive job. Initialized from server-fetched
  // initialActiveDeriveTargets and kept in sync after router.refresh() completes.
  // Optimistically includes any variant the user just triggered so the tab never
  // flickers back to "Generate" in the gap between setGeneratingVariant(null)
  // and the refresh completing.
  const [activeDeriveTargets, setActiveDeriveTargets] = useState<VariantType[]>(initialActiveDeriveTargets);
  useEffect(() => {
    setActiveDeriveTargets(initialActiveDeriveTargets);
  }, [initialActiveDeriveTargets]);

  // The active variant can disappear underneath us — the management modal can
  // delete it. `activeVariant` and `body` are local state, so a router.refresh()
  // alone would leave the page rendering a variant that no longer exists. Snap
  // back to whatever the server now resolves as active and drop the cached body
  // so the refreshed bodyPromise takes over.
  useEffect(() => {
    if (variants.some((v) => v.id === activeVariant.id)) return;
    setActiveVariant(initialActiveVariant);
    setBody(null);
    setSelectedServings(preferredServings ?? initialActiveVariant.servings);
  }, [variants, activeVariant.id, initialActiveVariant, preferredServings]);

  // Auto-open the meal-plan modal when arriving from /meal-plan empty-day cell
  // (e.g. /recipes/foo?addToDate=2026-04-30). Runs once on mount; clearing the
  // param from the URL prevents the modal from re-opening on refresh.
  useEffect(() => {
    if (addToDate) {
      setShowMealPlanModal(true);
      router.replace(window.location.pathname, { scroll: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock body scroll when any modal/overlay is open
  useEffect(() => {
    const isOpen = showMealPlanModal || showCookMode || showFabMenu || showAdminModal;
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showMealPlanModal, showCookMode, showFabMenu, showAdminModal]);

  // Is the viewer looking at English text that is supposed to be German?
  //
  // Two independent ways to be, and keying on only the first is what made this
  // silent (#887):
  //   1. the server said so (`translationPending`) — the recipe is HELD at
  //      pending_review while translate-recipe writes German, OR the shell it
  //      rendered fell back to English (#954). Available immediately, before
  //      the body streams in;
  //   2. the body we actually rendered fell back to English for at least one
  //      step. This is the case row status cannot see: the derive/iterate path
  //      promotes to published but never holds, so a published recipe can be
  //      missing German steps on one variant while another is complete. It also
  //      re-evaluates on a variant SWITCH, which is how the user meets that gap.
  const translationIncomplete =
    lang === 'de' && (translationPending || body?.usesEnglishFallback === true);

  // Freshly-imported recipe whose German translation is still being written:
  // tell the viewer it's on the way, then poll for completion and swap the
  // content in automatically — no manual reload. English viewers and
  // fully-translated recipes never poll. translate-recipe purges the recipe
  // caches before it promotes to published, and a fallback-filled body is never
  // cached at all (see getCachedRecipeBody), so the router.refresh() below
  // re-renders with the fresh German content.
  useEffect(() => {
    if (!translationIncomplete) return;

    const POLL_INTERVAL_MS = 4000;
    const MAX_ATTEMPTS = 90; // ~6 min at healthy cadence — covers the fast-retry repair passes
    // Absolute wall-clock bound (#754 follow-up): under failure backoff the
    // attempt budget alone would keep the "translating…" toast up for hours.
    // Twelve minutes is double the healthy window; past that the honest state
    // is "delayed", which the recover-translations cron handles anyway.
    const MAX_WALL_CLOCK_MS = 12 * 60_000;
    const startedAt = Date.now();
    let cancelled = false;
    let attempts = 0;
    let done = false;
    let consecutiveFailures = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // Persistent "translating…" toast — it stays up until we flip it to the
    // "done" message (which then self-dismisses after 4s) or dismiss it.
    const toastId = toast(t('notifications.translating'), { variant: 'info', duration: null });

    // Chained setTimeout, NOT setInterval (#754): the interval kept firing on
    // schedule while a slow response was still in flight, so during database
    // trouble this loop stacked requests on itself. Chaining caps it at one in
    // flight; failures stretch the cadence with jitter (the bell's backOff
    // shape, #711) instead of holding it against a struggling endpoint.
    const scheduleNext = () => {
      if (cancelled || done) return;
      // Shared backoff shape — see src/lib/utils/poll-backoff.ts (#754).
      timer = setTimeout(tick, pollBackoffDelay(POLL_INTERVAL_MS, consecutiveFailures));
    };

    const tick = async () => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS || Date.now() - startedAt >= MAX_WALL_CLOCK_MS) {
        dismissToast(toastId);
        return;
      }
      try {
        const res = await fetch(`/api/recipes/${meta.id}/translation-status`);
        if (!res.ok) {
          // 401/403/404 are TERMINAL, not transient. Translation state is only
          // observable to the owner or an admin, and since the watch can now be
          // raised by the rendered body (#887) a signed-out or non-owning German
          // viewer can reach a published recipe that is missing German — and
          // would otherwise sit on a "translating…" toast, polling a 403 for the
          // full 12-minute cap. Nothing they can do, nothing we can learn: drop
          // the toast and stop.
          if (res.status === 401 || res.status === 403 || res.status === 404) {
            done = true;
            dismissToast(toastId);
            return;
          }
          // transient (5xx, including the 503 the route answers when the
          // database is unreachable) — keep polling until the cap, backing off
          consecutiveFailures += 1;
          scheduleNext();
          return;
        }
        consecutiveFailures = 0;
        const data = (await res.json()) as { state: 'translating' | 'ready' | 'failed' };
        if (cancelled) return;
        if (data.state === 'ready') {
          done = true;
          // Flip the same toast to the success message; success variant
          // auto-dismisses after 4s. Then swap the content in place.
          updateToast(toastId, t('notifications.translationReady'), { variant: 'success' });

          // router.refresh() ALONE CANNOT DO THAT (#917). The rendered steps
          // and ingredients live in `body` state, and once it is populated the
          // render below takes the `body ? …` branch and never consults the
          // refreshed bodyPromise again. So the refresh repaints the shell in
          // German while the body keeps painting the English it resolved before
          // the translation landed — the user is told the recipe is fully
          // translated while looking at the evidence that it isn't. Worse, it
          // is self-silencing: `translationIncomplete` reads the stale body's
          // usesEnglishFallback, so the deps never change and this effect never
          // gets a second try.
          //
          // Fetch the body for the variant on screen explicitly.
          // getVariantDetail reads straight from the database, past the TTL
          // caches, so it cannot hand back the English that started this.
          const variantId = activeVariantRef.current.id;
          const fresh = await fetchVariantDetailAction(variantId, lang);
          if (cancelled) return;
          // Scoped to the variant we asked about: the user can switch tabs
          // while the fetch is in flight, and painting one variant's German
          // onto another is worse than leaving the English up.
          if (fresh && activeVariantRef.current.id === variantId) {
            setBody({
              steps: fresh.steps,
              ingredients: fresh.ingredients,
              usesEnglishFallback: fresh.usesEnglishFallback,
            });
          }
          // The shell — title, description, per-variant hero copy — is
          // server-rendered, so it still needs the refresh.
          router.refresh();
          return;
        } else if (data.state === 'failed') {
          // Repair passes exhausted — the recipe is held for background repair
          // (recover-translations cron). Be honest rather than vanishing, then
          // let the toast auto-dismiss. done=true so the post-teardown cleanup
          // doesn't yank the message early.
          done = true;
          updateToast(toastId, t('notifications.translationDelayed'), { variant: 'warning' });
          return;
        }
        scheduleNext();
      } catch {
        // network hiccup — keep polling until the cap, backing off
        consecutiveFailures += 1;
        scheduleNext();
      }
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      // Clear the persistent toast if we're tearing down BEFORE completion
      // (navigation away, cap reached, failure). After completion, leave the
      // success toast to auto-dismiss on its own — router.refresh() flips
      // translationPending to false, which re-runs this cleanup, and we must
      // not yank the "translated" confirmation early.
      if (!done) dismissToast(toastId);
    };
    // meta.id is stable for a mounted recipe; excluding toast/updateToast/
    // dismissToast/router/t keeps the toast from re-firing on identity churn.
    // `translationIncomplete` is a boolean, not the body object, so a body
    // update that doesn't change the answer can't restart the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationIncomplete, meta.id]);

  // A hero-image regeneration was just queued from the management hub (#642).
  // The job reports itself in the navbar bell and lands a notification when it
  // finishes, but a user still looking at the recipe deserves the answer here:
  // a persistent toast while it runs, then the new photo swapped in on the spot.
  //
  // The WATCHING is the hero's (#751) — `imageGenerationInFlight` below hands
  // the run to `useGeneratedImage`, which polls for a CHANGED url and calls
  // `handleImageResolved`. This effect only owns the toast.
  const imageRegenToastRef = useRef<{ id: string; settled: boolean } | null>(null);

  useEffect(() => {
    if (!imageRegen) return;

    const entry = {
      id: toast(t('notifications.imageGenerating'), { variant: 'info', duration: null }),
      settled: false,
    };
    imageRegenToastRef.current = entry;

    // Backstop, not a poll: the hero stops watching if the user switches to
    // another variant mid-run, and a duration-less toast must not outlive the
    // window the run could plausibly land in (the hero's own cap is the same
    // 5 minutes). The notification still arrives either way.
    const giveUp = setTimeout(() => {
      if (entry.settled) return;
      entry.settled = true;
      updateToast(entry.id, t('notifications.imageDelayed'), { variant: 'warning' });
      setImageRegen(null);
    }, IMAGE_REGEN_TIMEOUT_MS);

    return () => {
      clearTimeout(giveUp);
      // Only yank the toast if we're tearing down BEFORE it said anything
      // final — a resolved toast auto-dismisses on its own.
      if (!entry.settled) dismissToast(entry.id);
      if (imageRegenToastRef.current === entry) imageRegenToastRef.current = null;
    };
    // imageRegen is a fresh object per queued run, which is what re-arms this.
    // toast/updateToast/dismissToast/t are excluded so identity churn doesn't
    // restart the toast mid-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageRegen]);

  // Called by the hero when the regeneration it was watching ends: the new url,
  // or null when its poll gave up (attempt cap, or the recipe went away).
  function handleImageResolved(url: string | null) {
    const regen = imageRegen;
    const entry = imageRegenToastRef.current;
    if (!regen) return;
    if (entry && !entry.settled) {
      entry.settled = true;
      updateToast(
        entry.id,
        url ? t('notifications.imageReady') : t('notifications.imageDelayed'),
        { variant: url ? 'success' : 'warning' },
      );
    }
    // Scoped to the variant that was regenerated — the user may have switched
    // tabs while it ran, and the other variant's photo is unchanged. Held here
    // rather than only in the hero because the hero remounts per variant, and
    // a switch away and back would otherwise fall back to the stale server url.
    if (url) setFreshHeroImage({ variantId: regen.variantId, url });
    setImageRegen(null);
  }

  function handleVariantChange(variantType: VariantType) {
    const newVariant = variants.find((v) => v.variantType === variantType);
    if (!newVariant || newVariant.id === activeVariant.id) return;
    // Infeasible variants cannot be activated — they have no steps or ingredients.
    // The tab is disabled in the UI, so this is a defensive guard.
    if (newVariant.variantNotFeasible) return;

    startTransition(async () => {
      const result = await fetchVariantDetailAction(newVariant.id, lang);
      if (result) {
        setActiveVariant(newVariant);
        setBody({
          steps: result.steps,
          ingredients: result.ingredients,
          usesEnglishFallback: result.usesEnglishFallback,
        });
        setSelectedServings(preferredServings ?? newVariant.servings);
        // Only update URL after data arrives — prevents URL/content mismatch on SA failure.
        // `servings` is carried through so a reload after a tab switch still opens
        // on the planned portions (#743).
        const query =
          requestedServings != null
            ? `?variant=${variantType}&servings=${requestedServings}`
            : `?variant=${variantType}`;
        router.push(query, { scroll: false });
      }
    });
  }

  async function handleShare() {
    const outcome = await shareOrCopyLink({ url: window.location.href });
    if (outcome === 'copied') toast(t('recipes.shareLinkCopied'), { variant: 'success' });
    else if (outcome === 'failed') toast(t('recipes.shareLinkFailed'), { variant: 'error' });
    // 'shared' — native sheet handled feedback, no toast needed.
  }

  // VariantSelector clicks open the steering dialog instead of triggering
  // generation immediately. confirmGenerateVariant below does the actual work
  // once the user has dismissed/submitted the dialog.
  function handleGenerateVariant(targetVariant: VariantType, originRect: DOMRect) {
    setVariantGenError(null);
    setPendingNotes('');
    setGuideState(null);
    setPendingGenerate({ variantType: targetVariant, originRect });
  }

  function closeGenerateDialog() {
    setPendingGenerate(null);
    setGuideState(null);
  }

  // Kick off the proposal job and enter the loading phase. On a re-open the
  // action collides with the still-live job and hands back the same jobId, so
  // the poll below simply resumes it.
  async function requestGuideOptions() {
    if (!pendingGenerate) return;
    setGuideState({ phase: 'loading', jobId: '' });
    const result = await proposeVariantOptionsAction(meta.id, pendingGenerate.variantType);
    if (result.success) {
      setGuideState({ phase: 'loading', jobId: result.jobId });
    } else {
      setGuideState({ phase: 'error', error: result.error });
    }
  }

  function toggleGuideDirection(optionId: string, on: boolean) {
    setGuideState((prev) => {
      if (!prev || prev.phase !== 'options') return prev;
      const chosen = on
        ? [...new Set([...prev.chosen, optionId])]
        : prev.chosen.filter((id) => id !== optionId);
      return { ...prev, chosen };
    });
  }

  async function confirmGenerateVariant() {
    if (!pendingGenerate) return;
    const { variantType, originRect } = pendingGenerate;
    const notes = pendingNotes.trim() || undefined;
    // Fold the reviewed directions (if the guide flow ran) into the request as
    // self-contained one-liners — the derive prompt needs no access to the
    // proposal job.
    let directions: { accepted: string[]; rejected: string[] } | undefined;
    if (guideState?.phase === 'options' && guideState.chosen.length > 0) {
      // Only meaningful once something is switched on — an all-off set is
      // "no opinion", not "reject everything".
      directions = {
        accepted: guideState.options
          .filter((o) => guideState.chosen.includes(o.id))
          .map(variantOptionToDirection),
        rejected: guideState.options
          .filter((o) => !guideState.chosen.includes(o.id))
          .map(variantOptionToDirection),
      };
    }
    closeGenerateDialog();
    setGeneratingVariant(variantType);
    const result = await generateVariantAction(meta.id, variantType, notes, directions);
    if (result.success) {
      // Hand off to the navbar bell — fly a pill from the clicked tab to the
      // notification bell so the user sees where their job has gone, then
      // refresh the page so the server-side derive-target list picks up the
      // new active job and keeps the tab in its "Generating" state across
      // navigation.
      flyToBell(originRect, t(`recipes.variants.${variantType}`));
      // Wake the bell's poller so the progress ring appears immediately
      // instead of waiting up to 30s for the next idle tick.
      wakeNotificationBell();
      // Optimistically mark this variant as deriving so the tab stays in
      // "Generating…" state even before router.refresh() delivers updated
      // server props. Without this, the tab briefly flickers back to "Generate"
      // in the gap between setGeneratingVariant(null) and the refresh completing.
      setActiveDeriveTargets((prev) => [...new Set([...prev, variantType])]);
      router.refresh();
      setGeneratingVariant(null);
    } else {
      setVariantGenError(result.error);
      setGeneratingVariant(null);
    }
  }

  // Poll the proposal job while the steering dialog is in its loading phase.
  // Same chained-setTimeout shape as the translation watch above (#754): one
  // request in flight, failure-stretched cadence, hard wall-clock cap.
  const guideJobId = guideState?.phase === 'loading' && guideState.jobId ? guideState.jobId : null;
  useEffect(() => {
    if (!guideJobId) return;

    const POLL_INTERVAL_MS = 2500;
    const MAX_WALL_CLOCK_MS = 4 * 60_000; // proposal task maxDuration is 120s + retries
    const startedAt = Date.now();
    let cancelled = false;
    let consecutiveFailures = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fail = (error: string | null) => {
      if (!cancelled) setGuideState({ phase: 'error', error });
    };

    const scheduleNext = () => {
      if (cancelled) return;
      timer = setTimeout(tick, pollBackoffDelay(POLL_INTERVAL_MS, consecutiveFailures));
    };

    const tick = async () => {
      if (Date.now() - startedAt >= MAX_WALL_CLOCK_MS) {
        fail(null);
        return;
      }
      try {
        const res = await fetch(`/api/jobs/${guideJobId}/status`);
        if (!res.ok) {
          // Auth/ownership/404 are terminal; 5xx (incl. the 503 the route
          // answers when the database is unreachable) is transient.
          if (res.status === 401 || res.status === 403 || res.status === 404) {
            fail(null);
            return;
          }
          consecutiveFailures += 1;
          scheduleNext();
          return;
        }
        consecutiveFailures = 0;
        const data = (await res.json()) as {
          status: string;
          errorMessage?: string | null;
          variantOptions?: VariantOption[] | null;
        };
        if (cancelled) return;
        if (data.status === 'completed') {
          if (data.variantOptions && data.variantOptions.length > 0) {
            setGuideState({ phase: 'options', options: data.variantOptions, chosen: [] });
          } else {
            fail(null);
          }
          return;
        }
        if (data.status === 'failed' || data.status === 'timed_out' || data.status === 'cancelled') {
          fail(data.errorMessage ?? null);
          return;
        }
        scheduleNext();
      } catch {
        consecutiveFailures += 1;
        scheduleNext();
      }
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [guideJobId]);

  const variantTabs = variants.map((v) => ({
    variantType: v.variantType,
    label: t(`recipes.variants.${v.variantType}`),
    // Infeasible variants have no metrics — surface zeros (the selector hides the meta line).
    prepTimeMinutes: v.prepTimeMinutes ?? 0,
    cookTimeMinutes: v.cookTimeMinutes ?? 0,
    // Infeasible rows render as disabled tabs with a tooltip, not as active.
    available: !v.variantNotFeasible,
    notFeasible: v.variantNotFeasible,
    notFeasibleReason: v.variantNotFeasibleReason,
  }));

  // Management is always scoped to the active variant — surface that on the
  // button (and the modal repeats it in its header).
  const manageLabel = t('recipes.manage.buttonWithVariant', {
    variant: t(`recipes.variants.${activeVariant.variantType}`),
  });

  // Save-to-cookbook lives on the hero image (top-left corner), opposite Share.
  // Only offered to signed-in viewers — the underlying action requires auth.
  const saveButton = isLoggedIn ? (
    <CookbookPicker
      recipeId={meta.id}
      variantType={activeVariant.variantType}
      initialCookbooks={initialCookbooks}
      triggerClassName={styles.heroOverlayBtn}
      triggerLabel={t('recipes.saveToCookbook')}
    >
      <Icon name="bookmark" size={18} strokeWidth={1.75} />
    </CookbookPicker>
  ) : null;

  // Sign-in gate — shown to logged-out visitors wherever an action cluster
  // would otherwise sit (the actions all hit auth-only Server Actions).
  const signInGate = (
    <div className={styles.signInGate} role="note">
      <span className={styles.signInGateText}>{t('recipes.signInGate')}</span>
      <Button as={Link} href="/auth/login" variant="primary" size="sm" leadingIcon="user">
        {t('nav.signIn')}
      </Button>
    </div>
  );

  // Hero action cluster — shared by desktop (controls slot) and mobile
  // (mobileActions). Add-to-meal-plan is the labelled accent primary; Start
  // Cooking + Manage are icon-only secondaries (Save moved to the image).
  // Logged-out visitors get the sign-in gate instead.
  const heroActions = isLoggedIn ? (
    <div className={styles.actionBar}>
      <Button variant="primary" leadingIcon="calendar" onClick={() => setShowMealPlanModal(true)}>
        {t('recipes.addToMealPlan')}
      </Button>
      <Button
        variant="secondary"
        iconOnly
        leadingIcon="play"
        onClick={() => setShowCookMode(true)}
        disabled={!body}
        aria-label={t('recipes.startCooking')}
        title={t('recipes.startCooking')}
      />
      {canManage && (
        <Button
          variant="secondary"
          iconOnly
          leadingIcon="settings"
          onClick={() => setShowAdminModal(true)}
          aria-label={manageLabel}
          title={manageLabel}
        />
      )}
    </div>
  ) : (
    signInGate
  );

  return (
    <>
    {/* CookMode is gated on `body` — the "Start Cooking" button is also disabled
        until the initial body data lands, so this renders as soon as the user
        could meaningfully open it. */}
    {showCookMode && body && (
      <CookMode
        steps={body.steps}
        ingredients={body.ingredients}
        selectedServings={selectedServings}
        baseServings={activeVariant.servings}
        locale={lang}
        unitSystem={unitSystem}
        title={activeVariant.titleOverride ?? meta.title}
        onClose={() => setShowCookMode(false)}
      />
    )}
    {showMealPlanModal && (
      <AddToMealPlanModal
        recipeId={meta.id}
        recipeTitle={meta.title}
        recipeReady={meta.status === 'published'}
        variantType={activeVariant.variantType}
        baseServings={activeVariant.servings}
        initialServings={selectedServings}
        defaultDate={addToDate}
        onClose={() => setShowMealPlanModal(false)}
      />
    )}
    {showAdminModal && (
      <RecipeAdminModal
        recipeId={meta.id}
        recipeSlug={meta.slug}
        recipeTitle={meta.title}
        variantId={activeVariant.id}
        variantType={activeVariant.variantType}
        variantLabel={t(`recipes.variants.${activeVariant.variantType}`)}
        variantImageUrl={heroImageUrl}
        variants={variants.map((v) => ({
          variantType: v.variantType,
          isDefault: v.isDefault,
          notFeasible: v.variantNotFeasible,
        }))}
        status={meta.status}
        info={{
          qualityScore: meta.qualityScore,
          dietaryConfidence: meta.dietaryConfidence,
          sourceUrl: meta.sourceUrl,
          sourceType: meta.sourceType,
          createdAt: meta.createdAt,
          updatedAt: meta.updatedAt,
        }}
        lang={lang}
        isOwner={isOwner}
        isAdmin={isAdmin}
        onImageQueued={() => setImageRegen({ variantId: activeVariant.id })}
        onClose={() => setShowAdminModal(false)}
      />
    )}
    <article className={styles.article}>
      <div className={styles.selectorBand}>
      <VariantSelector
        variants={variantTabs}
        activeVariant={activeVariant.variantType}
        onVariantChange={handleVariantChange}
        // Deriving a variant requires auth — logged-out visitors see empty
        // variants as plain "not generated" tabs, not Generate CTAs.
        onGenerate={isLoggedIn ? handleGenerateVariant : undefined}
        generatingVariant={generatingVariant}
        derivingVariants={activeDeriveTargets}
      />
      </div>

      {/* Variant generation error — surfaced inline under the selector */}
      {variantGenError && (
        <div className={styles.variantGenError} role="alert">
          {variantGenError}
        </div>
      )}

      {/* Variant-steering dialog. Uses the canonical Modal primitive (#128)
          which handles fixed-centred positioning, focus trap, scrim, ESC, and
          scroll lock — replaces the bare <dialog> that anchored top-left on
          mobile. autoFocus on the textarea was dropped because the focus ring
          appearing on mount reads as an error state. */}
      <Modal
        open={pendingGenerate !== null}
        onClose={closeGenerateDialog}
        // The plain dialog is a paragraph + a textarea and sits fine at md.
        // Once the guide flow is entered it carries multi-line direction
        // cards, which need the wider measure to stay scannable.
        size={guideState === null ? 'md' : 'lg'}
        labelledBy="variant-steer-title"
      >
        {pendingGenerate && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void confirmGenerateVariant();
            }}
          >
            <ModalHeader id="variant-steer-title" onClose={closeGenerateDialog}>
              {t('recipes.variantSteerTitle', {
                label: t(`recipes.variants.${pendingGenerate.variantType}`),
              })}
            </ModalHeader>
            <ModalBody>
              {guideState === null && (
                <>
                  <p className={styles.variantSteerDescription}>
                    {t('recipes.variantSteerDescription')}
                  </p>
                  {/* The interactive path (#909): propose concrete directions
                      first, decide, THEN spend the full derivation. */}
                  <Button
                    variant="secondary"
                    fullWidth
                    leadingIcon="sparkles"
                    className={styles.guideCta}
                    onClick={() => void requestGuideOptions()}
                  >
                    {t('recipes.variantGuide.cta')}
                  </Button>
                </>
              )}

              {guideState?.phase === 'loading' && (
                <p className={styles.guideLoading} role="status">
                  <Icon name="loading" size={16} data-icon-spin className={styles.guideLoadingIcon} />
                  {t('recipes.variantGuide.loading')}
                </p>
              )}

              {guideState?.phase === 'error' && (
                <>
                  <p className={styles.variantSteerDescription} role="alert">
                    {t('recipes.variantGuide.failed')}
                  </p>
                  {guideState.error && <ActionError raw={guideState.error} className={styles.guideError} />}
                </>
              )}

              {guideState?.phase === 'options' && (
                <>
                  <p className={styles.variantSteerDescription}>
                    {t('recipes.variantGuide.intro')}
                  </p>
                  <DirectionCards
                    options={guideState.options}
                    chosen={guideState.chosen}
                    onToggle={toggleGuideDirection}
                    toggleLabel={(opt) =>
                      t('recipes.variantGuide.toggleAria', { title: opt.title })
                    }
                  />
                  <p className={styles.guideHint}>{t('recipes.variantGuide.hint')}</p>
                </>
              )}

              {guideState?.phase !== 'loading' && (
                <Textarea
                  value={pendingNotes}
                  onChange={(e) => setPendingNotes(e.currentTarget.value)}
                  placeholder={t('recipes.variantSteerPlaceholder')}
                  maxLength={500}
                  rows={3}
                />
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={closeGenerateDialog}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary">
                {t('recipes.variantGenerate')}
              </Button>
            </ModalFooter>
          </form>
        )}
      </Modal>


      {/* "Why this variant" note — explains the tradeoff the active variant
          makes. Shown for whichever of Quick/Classic/Bougie is selected. */}
      {activeVariant.variantNote && (
        <NotePanel
          className={styles.variantNote}
          label={t('recipes.variantNoteLabel')}
          ariaLabel={t('recipes.variantNoteAria')}
        >
          {activeVariant.variantNote}
        </NotePanel>
      )}

      {/* Hero — controls slot is visible in the right column on desktop only.
          Mobile users access the same controls via the FAB. */}
      <RecipeHero
        title={activeVariant.titleOverride ?? meta.title}
        authorName={authorName}
        authorUserId={meta.createdByUserId}
        sourceType={meta.sourceType}
        sourceUrl={meta.sourceUrl}
        description={activeVariant.descriptionOverride ?? meta.description}
        imageUrl={heroImageUrl}
        recipeId={meta.id}
        variantType={activeVariant.variantType}
        // Only the variant actually being re-shot watches. A switch away pauses
        // the watch (the hero remounts per variant); coming back re-arms it
        // against whatever is on screen then, and the toast's own backstop
        // covers a run that lands while the user is looking elsewhere.
        imageGenerationInFlight={imageRegen?.variantId === activeVariant.id}
        onImageResolved={handleImageResolved}
        slug={meta.slug}
        prepTimeMinutes={activeVariant.prepTimeMinutes}
        cookTimeMinutes={activeVariant.cookTimeMinutes}
        servings={selectedServings}
        baseServings={activeVariant.servings}
        batchBoundMinutes={activeVariant.batchBoundMinutes}
        waitMinutes={activeVariant.waitMinutes}
        servingUnit={activeVariant.servingUnit}
        caloriesPerServing={activeVariant.nutrition?.calories ?? null}
        categoryName={meta.categoryName}
        isVegan={meta.isVegan}
        isVegetarian={meta.isVegetarian}
        isGlutenFree={meta.isGlutenFree}
        isDairyFree={meta.isDairyFree}
        isHashimotoFriendly={meta.isHashimotoFriendly}
        hashimotoScore={meta.hashimotoScore}
        showHashimotoScore={activeDietaryPreferences.includes('hashimoto')}
        onServingsClick={() => setShowFabMenu(true)}
        imageOverlayActionsStart={saveButton}
        imageOverlayActions={
          isShareable ? (
            <button
              type="button"
              className={styles.heroOverlayBtn}
              onClick={handleShare}
              aria-label={t('recipes.shareAria')}
              title={t('recipes.share')}
            >
              <Icon name="share" size={18} strokeWidth={1.75} />
            </button>
          ) : null
        }
        mobileActions={heroActions}
        servingsControl={
          <ServingCounter value={selectedServings} onChange={setSelectedServings} compact />
        }
        controls={heroActions}
      />

      {/* Remix cross-links (#614, restyled #646).
          Placed directly under the hero, NOT under the variant selector: there
          it sat between a segmented control and the dish photograph, which is
          the "fourth variant" misread the element must avoid — and it asked
          "cook something else?" before the reader had even seen this dish.
          Here the hero has already answered what the dish is, so a reader who
          just learned it contains veal is at the exact moment of "then not
          this one". On phone it is a requirement rather than a preference:
          below this comes the sticky ingredients/method tablist, and anything
          under that lives in a tab panel half the audience never opens. */}
      {/* The heading has to be true of what the band actually holds (#664).
          On a derivative the band is only the back-link, and a heading reading
          "other recipes made from this" claimed the opposite of the single card
          under it — so there the cards speak for themselves and the label lives
          on the section for assistive tech only. */}
      {(lineage.parent || lineage.derivatives.length > 0) && (
        <section
          className={styles.lineageBand}
          {...(lineage.derivatives.length > 0
            ? { 'aria-labelledby': 'lineage-heading' }
            : { 'aria-label': t('recipes.lineage.basedOn') })}
        >
          {lineage.derivatives.length > 0 && (
            <h2 id="lineage-heading" className={styles.lineageLabel}>
              {lineage.parent
                ? t('recipes.lineage.relatedLabel')
                : t('recipes.lineage.bandLabel')}
            </h2>
          )}
          <div className={styles.lineageGrid}>
            {lineage.derivatives.map((d) => (
              <RelatedRecipeLink
                key={d.slug}
                slug={d.slug}
                title={d.title}
                imageUrl={d.imageUrl}
                dietaryItems={dietaryItemsFor(d)}
                dietaryLabel={t('recipes.dietaryInformation')}
                eyebrow={
                  d.derivationNote
                    ? t('recipes.lineage.versionOf', { note: derivationLabel(d.derivationNote) })
                    : t('recipes.lineage.derivedVersion')
                }
                ariaLabel={t('recipes.lineage.derivedAria', { title: d.title })}
              />
            ))}
            {lineage.parent && (
              <RelatedRecipeLink
                slug={lineage.parent.slug}
                title={lineage.parent.title}
                imageUrl={lineage.parent.imageUrl}
                direction="backward"
                eyebrow={t('recipes.lineage.basedOn')}
                ariaLabel={t('recipes.lineage.basedOnAria', { title: lineage.parent.title })}
              />
            )}
          </div>
        </section>
      )}

      {/* Bottom sheet is opened from the hero's Serves meta tap on mobile (see RecipeHero onServingsClick). */}
      {showFabMenu && (
        <div className={styles.fabScrim} onClick={() => setShowFabMenu(false)}>
          <div className={styles.fabSheet} onClick={e => e.stopPropagation()}>
            <div className={styles.fabSheetHandle} />
            <div className={styles.fabSheetHeader}>
              <span className={styles.fabSheetTitle}>{t('recipes.servingsAndActions')}</span>
              <button type="button" className={styles.fabSheetClose} onClick={() => setShowFabMenu(false)} aria-label={t('common.close')}>
                <Icon name="x" size={18} strokeWidth={2} />
              </button>
            </div>
            <div className={styles.fabServings}>
              <ServingCounter value={selectedServings} onChange={setSelectedServings} />
            </div>
            <div className={styles.fabSheetActions}>
              {isLoggedIn ? (
                <>
                  <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    leadingIcon="play"
                    disabled={!body}
                    onClick={() => { setShowCookMode(true); setShowFabMenu(false); }}
                  >
                    {t('recipes.startCooking')}
                  </Button>
                  <Button
                    variant="secondary"
                    fullWidth
                    leadingIcon="calendar"
                    onClick={() => { setShowMealPlanModal(true); setShowFabMenu(false); }}
                  >
                    {t('recipes.addToMealPlan')}
                  </Button>
                </>
              ) : (
                signInGate
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab panel content — aria-labelledby points to the active tab button. */}
      <div
        role="tabpanel"
        id={`tabpanel-${activeVariant.variantType}`}
        aria-labelledby={`tab-${activeVariant.variantType}`}
        className={styles.panel}
      >
        {isPending ? (
          <BodySkeleton />
        ) : body ? (
          <BodyContent
            steps={body.steps}
            ingredients={body.ingredients}
            activeVariant={activeVariant}
            selectedServings={selectedServings}
            activeDietaryPreferences={activeDietaryPreferences}
            techniqueInsight={activeVariant.techniqueInsight ?? meta.techniqueInsight}
            lang={lang}
            unitSystem={unitSystem}
            mobileSection={mobileSection}
            setMobileSection={setMobileSection}
          />
        ) : (
          <Suspense fallback={<BodySkeleton />}>
            <BodyResolver
              promise={bodyPromise}
              onResolve={setBody}
              activeVariant={activeVariant}
              selectedServings={selectedServings}
              activeDietaryPreferences={activeDietaryPreferences}
              techniqueInsight={activeVariant.techniqueInsight ?? meta.techniqueInsight}
              lang={lang}
              unitSystem={unitSystem}
              mobileSection={mobileSection}
              setMobileSection={setMobileSection}
            />
          </Suspense>
        )}
      </div>
    </article>
    </>
  );
}

// ---------------------------------------------------------------------------
// Body — split out so the heavy DB-backed steps/ingredients can suspend
// independently of the hero/selector during the View Transition.
// ---------------------------------------------------------------------------

interface BodyContentProps {
  steps: StepWithTranslation[];
  ingredients: IngredientWithDetails[];
  activeVariant: FeasibleVariantMetadata;
  selectedServings: number;
  activeDietaryPreferences: string[];
  techniqueInsight: string | null;
  lang: 'en' | 'de';
  unitSystem: 'metric' | 'imperial';
  mobileSection: 'ingredients' | 'steps';
  setMobileSection: (section: 'ingredients' | 'steps') => void;
}

function BodyResolver({
  promise,
  onResolve,
  ...rest
}: { promise: Promise<RecipeBody>; onResolve: (data: RecipeBody) => void } & Omit<BodyContentProps, 'steps' | 'ingredients'>) {
  // Suspends until bodyPromise resolves; React reuses the same Promise on
  // re-renders so this never re-suspends the active variant.
  const data = use(promise);
  // Lift the resolved body into parent state so CookMode and any subsequent
  // re-renders bypass the Suspense path entirely.
  useEffect(() => {
    onResolve(data);
  }, [data, onResolve]);
  return <BodyContent steps={data.steps} ingredients={data.ingredients} {...rest} />;
}

function BodyContent({
  steps,
  ingredients,
  activeVariant,
  selectedServings,
  activeDietaryPreferences,
  techniqueInsight,
  lang,
  unitSystem,
  mobileSection,
  setMobileSection,
}: BodyContentProps) {
  const t = useTranslations();
  const [showKcal, setShowKcal] = useState(false);
  const anyKcalAvailable = hasDisplayableKcal(ingredients, selectedServings, activeVariant.servings);
  return (
    <>
      {/* Mobile-only section switcher: Ingredients | Steps */}
      <div className={styles.mobileTabs} role="tablist" aria-label={t('recipes.sectionsAria')}>
        <button
          id="recipe-tab-ingredients"
          role="tab"
          type="button"
          aria-selected={mobileSection === 'ingredients'}
          aria-controls="recipe-panel-ingredients"
          className={`${styles.mobileTab} ${mobileSection === 'ingredients' ? styles.mobileTabActive : ''}`}
          onClick={() => setMobileSection('ingredients')}
        >
          {t('recipes.ingredients')}
        </button>
        <button
          id="recipe-tab-steps"
          role="tab"
          type="button"
          aria-selected={mobileSection === 'steps'}
          aria-controls="recipe-panel-steps"
          className={`${styles.mobileTab} ${mobileSection === 'steps' ? styles.mobileTabActive : ''}`}
          onClick={() => setMobileSection('steps')}
        >
          {t('recipes.method')}
        </button>
      </div>

      <div className={styles.twoCol} data-mobile-section={mobileSection}>
        {/* Left column: ingredients + nutrition */}
        <div
          id="recipe-panel-ingredients"
          className={styles.sidebar}
          role="tabpanel"
          aria-labelledby="recipe-tab-ingredients"
          tabIndex={0}
        >
          <section aria-labelledby="ingredients-heading">
            <div className={styles.sectionHeader}>
              <h2 id="ingredients-heading" className={styles.sectionTitle}>{t('recipes.ingredients')}</h2>
              {anyKcalAvailable && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={showKcal}
                  className={styles.kcalSwitch}
                  data-on={showKcal ? 'true' : undefined}
                  onClick={() => setShowKcal((v) => !v)}
                >
                  <Icon name="flame" size={12} strokeWidth={1.75} />
                  {t('ingredients.kcalToggleOff')}
                </button>
              )}
            </div>
            <IngredientListStagger>
              <IngredientList
                ingredients={ingredients}
                selectedServings={selectedServings}
                baseServings={activeVariant.servings}
                activeDietaryPreferences={activeDietaryPreferences}
                locale={lang}
                unitSystem={unitSystem}
                showKcal={showKcal}
              />
            </IngredientListStagger>
          </section>

          {/* Nutrition always renders — a freshly-derived variant whose
              nutrition hasn't been computed yet is handled inside the panel,
              which shows its own explicit note rather than the macro rows. */}
          <section aria-labelledby="nutrition-heading" className={styles.nutritionSection}>
            <h2 id="nutrition-heading" className={styles.sectionTitle}>{t('recipes.nutrition')}</h2>
            <NutritionPanel
              calories={activeVariant.nutrition?.calories ?? null}
              proteinG={activeVariant.nutrition?.proteinG ?? 0}
              carbsG={activeVariant.nutrition?.carbsG ?? 0}
              fatG={activeVariant.nutrition?.fatG ?? 0}
              fiberG={activeVariant.nutrition?.fiberG ?? 0}
              ingredients={ingredients}
              servings={activeVariant.servings}
              servingUnit={activeVariant.servingUnit}
              isEstimated={activeVariant.nutrition?.isEstimated ?? false}
            />
          </section>
        </div>

        {/* Right column: steps */}
        <div
          id="recipe-panel-steps"
          className={styles.main}
          role="tabpanel"
          aria-labelledby="recipe-tab-steps"
          tabIndex={0}
        >
          <section aria-labelledby="steps-heading">
            <h2 id="steps-heading" className={styles.sectionTitle}>{t('recipes.method')}</h2>
            <StepList
              steps={steps}
              ingredients={ingredients}
              selectedServings={selectedServings}
              baseServings={activeVariant.servings}
              locale={lang}
              unitSystem={unitSystem}
            />
          </section>

          {/* Technique insight — "Chef's note" */}
          {techniqueInsight && (
            <NotePanel
              className={styles.techniqueInsight}
              label={t('recipes.chefsNoteLabel')}
              ariaLabel={t('recipes.chefsNoteAria')}
              italic
            >
              {techniqueInsight}
            </NotePanel>
          )}
        </div>
      </div>
    </>
  );
}

function BodySkeleton() {
  return (
    <SkeletonGroup label="Loading recipe details…">
      <Skeleton height="40px" />
      <Skeleton height="200px" />
      <Skeleton height="300px" />
    </SkeletonGroup>
  );
}
